import React, { useEffect, useState } from "react";
import type { Feature } from "geojson";
import { observer } from "mobx-react-lite";
import { toast } from "react-toastify";

import Dialog from "@components/Dialog";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Row, Column } from "@components/Flex";
import Button from "@components/Button";
import Input from "@components/Input";
import Spinner from "@components/Spinner";
import Select from "@components/Select";
import { useLang } from "@src/hooks/useLang";
import {
  parseBoundaryFile,
  numericProperties,
  BoundaryParseError,
  TBoundaryProperty,
} from "@src/services/boundaryFileService";
import {
  buildCustomLayerFeatures,
  createCustomLayer,
  MAX_CUSTOM_LAYER_PAYLOAD_BYTES,
  type TCustomLayerSource,
} from "@src/services/customLayersService";
import {
  getJohnDeereStatus,
  getJohnDeereOrganizations,
  getJohnDeereFiles,
  downloadJohnDeereFile,
  type JohnDeereOrganization,
  type JohnDeereFile,
} from "@src/services/johnDeereService";
import BoundaryPreviewMap from "./BoundaryPreviewMap";

interface IProps {
  visible: boolean;
  fieldId: string;
  onClose: () => void;
  onAdded: (layerId: string) => void;
}

// Mirrors UploadFromFileDialog's wizard, asking a different question of the
// same file: not "which attribute is the field name" but "which attribute is
// the number to colour by".
type TStep =
  | "source"
  | "jdPick"
  | "parsing"
  | "selectValue"
  | "ready";

const SELECTABLE_STYLE = (selected: boolean): React.CSSProperties => ({
  padding: "8px 12px",
  borderRadius: 8,
  border: selected ? "2px solid #68ad18" : "1px solid #e5e7eb",
  cursor: "pointer",
});

const AddCustomLayerDialog: React.FC<IProps> = ({
  visible,
  fieldId,
  onClose,
  onAdded,
}) => {
  const lang = useLang();
  const fieldsLang = lang.fields;

  const [step, setStep] = useState<TStep>("source");
  const [source, setSource] = useState<TCustomLayerSource>("file");
  const [features, setFeatures] = useState<Feature[]>([]);
  const [candidates, setCandidates] = useState<TBoundaryProperty[]>([]);
  const [valueProperty, setValueProperty] = useState<string | null>(null);
  const [layerName, setLayerName] = useState("");
  const [unitLabel, setUnitLabel] = useState("");
  const [sourceFilename, setSourceFilename] = useState("");
  const [externalRef, setExternalRef] = useState<string | undefined>();
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);

  // John Deere picker state
  const [orgs, setOrgs] = useState<JohnDeereOrganization[]>([]);
  const [orgUri, setOrgUri] = useState("");
  const [jdFiles, setJdFiles] = useState<JohnDeereFile[]>([]);
  const [jdLoading, setJdLoading] = useState(false);

  // Checked here rather than read from a store: nothing else in the app holds
  // integration status globally, and the call is silent — a failure just means
  // "not connected" and the John Deere button stays hidden.
  const [deereConnected, setDeereConnected] = useState(false);

  useEffect(() => {
    if (!visible) return;
    let cancelled = false;
    getJohnDeereStatus()
      .then((s) => {
        if (!cancelled) setDeereConnected(Boolean(s.connected));
      })
      .catch(() => {
        if (!cancelled) setDeereConnected(false);
      });
    return () => {
      cancelled = true;
    };
  }, [visible]);

  // Fresh wizard every time the dialog opens.
  useEffect(() => {
    if (!visible) return;
    setStep("source");
    setSource("file");
    setFeatures([]);
    setCandidates([]);
    setValueProperty(null);
    setLayerName("");
    setUnitLabel("");
    setSourceFilename("");
    setExternalRef(undefined);
    setError("");
    setImporting(false);
    setOrgs([]);
    setOrgUri("");
    setJdFiles([]);
  }, [visible]);

  const errorMessage = (e: unknown): string => {
    if (e instanceof BoundaryParseError) {
      const localized = fieldsLang[`uploadError_${e.code}`];
      if (typeof localized === "string") {
        const showDetail =
          e.code === "singleShapefileComponent" || e.code === "missingShapefileParts";
        return showDetail && e.detail ? `${localized} (${e.detail})` : localized;
      }
      return e.message;
    }
    return e instanceof Error ? e.message : String(e);
  };

  /** Shared tail of both paths: parse, then ask which column holds the value. */
  const ingest = async (file: File, from: TCustomLayerSource, ref?: string) => {
    setError("");
    setSource(from);
    setSourceFilename(file.name);
    setExternalRef(ref);
    setStep("parsing");
    try {
      const parsed = await parseBoundaryFile(file);
      const numeric = numericProperties(parsed.features);
      setFeatures(parsed.features);
      setCandidates(numeric);
      setValueProperty(numeric.length > 0 ? numeric[0].key : null);
      // Seed the name from the filename — it is nearly always what the grower
      // calls the layer anyway, and they can edit it on the next step.
      setLayerName(file.name.replace(/\.(zip|kml|kmz|shp)$/i, "").slice(0, 80));
      setStep(numeric.length > 0 ? "selectValue" : "ready");
    } catch (e) {
      setError(errorMessage(e));
      setStep("source");
    }
  };

  const onSetFile = async ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    const file = target.files?.[0];
    target.value = ""; // allow re-selecting the same file
    if (!file) return;
    await ingest(file, "file");
  };

  const openJohnDeere = async () => {
    setError("");
    setStep("jdPick");
    setJdLoading(true);
    try {
      const res = await getJohnDeereOrganizations();
      const list = res.organizations ?? [];
      setOrgs(list);
      if (list.length > 0) {
        setOrgUri(list[0].selfUri);
        await loadJdFiles(list[0].selfUri);
      }
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setJdLoading(false);
    }
  };

  const loadJdFiles = async (uri: string) => {
    setJdLoading(true);
    try {
      const res = await getJohnDeereFiles(uri);
      setJdFiles(res.files ?? []);
    } catch (e) {
      setError(errorMessage(e));
      setJdFiles([]);
    } finally {
      setJdLoading(false);
    }
  };

  const pickJdFile = async (file: JohnDeereFile) => {
    setJdLoading(true);
    try {
      const blob = await downloadJohnDeereFile(file.id);
      const orgId = orgUri.split("/").filter(Boolean).pop() ?? "";
      // Wrapped as a File so it goes through the very same parser a manual
      // upload uses — one parser, one set of error messages.
      await ingest(
        new File([blob], file.name),
        "johndeere",
        `johndeere:${orgId}:${file.id}`
      );
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setJdLoading(false);
    }
  };

  const handleImport = async () => {
    if (features.length === 0 || layerName.trim() === "") return;
    setImporting(true);
    try {
      const payload = {
        name: layerName.trim(),
        source,
        sourceFilename: sourceFilename || undefined,
        valueProperty,
        unitLabel: unitLabel.trim() || null,
        externalRef,
        features: buildCustomLayerFeatures(features, valueProperty),
      };
      // Checked here so an over-large prescription gives advice rather than an
      // opaque 413 from the body-size limit.
      if (JSON.stringify(payload).length > MAX_CUSTOM_LAYER_PAYLOAD_BYTES) {
        setError(
          fieldsLang.customLayerTooLarge
        );
        setImporting(false);
        return;
      }
      const layer = await createCustomLayer(fieldId, payload);
      toast.success(fieldsLang.customLayerImported);
      onAdded(layer.id);
      onClose();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setImporting(false);
    }
  };

  const startOver = () => {
    setStep("source");
    setFeatures([]);
    setCandidates([]);
    setValueProperty(null);
    setError("");
  };

  const errorBlock = error ? (
    <>
      <SizedBox height={16} />
      <Text size={14} color="#e53e3e" style={{ whiteSpace: "pre-wrap" }}>
        {error}
      </Text>
    </>
  ) : null;

  const renderSource = () => (
    <>
      <Text style={{ whiteSpace: "pre-wrap" }}>
        {fieldsLang.customLayerDialogText}
      </Text>
      <SizedBox height={24} />
      <input type="file" accept=".kml,.kmz,.zip" onChange={onSetFile} />
      {deereConnected && (
        <>
          <SizedBox height={20} />
          <Text size={14} color="#6B7280">
            {fieldsLang.customLayerOr}
          </Text>
          <SizedBox height={8} />
          <Button kind="outline" onClick={openJohnDeere}>
            {fieldsLang.customLayerFromDeere}
          </Button>
        </>
      )}
      {errorBlock}
      <SizedBox height={32} />
      <Row justifyContent="flex-end">
        <Button onClick={onClose} kind="outline">
          {fieldsLang.uploadDialogCancelButton}
        </Button>
      </Row>
    </>
  );

  const renderJdPick = () => (
    <>
      <Text weight={500}>
        {fieldsLang.customLayerJdPickTitle}
      </Text>
      <SizedBox height={12} />
      {orgs.length > 1 && (
        <>
          <Select
            label={fieldsLang.customLayerJdOrg}
            value={orgUri}
            onChange={(uri: string) => {
              setOrgUri(uri);
              void loadJdFiles(uri);
            }}
            options={orgs.map((o) => ({ title: o.name, value: o.selfUri }))}
          />
          <SizedBox height={16} />
        </>
      )}
      {jdLoading ? (
        <Column crossAxisSize="max" alignItems="center" style={{ padding: 24 }}>
          <Spinner />
        </Column>
      ) : jdFiles.length === 0 ? (
        <Text size={14} color="#6B7280">
          {fieldsLang.customLayerJdEmpty}
        </Text>
      ) : (
        <Column crossAxisSize="max" style={{ gap: 8, maxHeight: 280, overflowY: "auto" }}>
          {jdFiles.map((f) => (
            <div key={f.id} onClick={() => pickJdFile(f)} style={SELECTABLE_STYLE(false)}>
              <Text size={14} weight={500}>
                {f.name}
              </Text>
              {f.createdTime && (
                <Text size={12} color="#6B7280">
                  {new Date(f.createdTime).toLocaleDateString()}
                </Text>
              )}
            </div>
          ))}
        </Column>
      )}
      {errorBlock}
      <SizedBox height={24} />
      <Row justifyContent="start">
        <Button kind="outline" onClick={startOver}>
          {fieldsLang.uploadStartOver}
        </Button>
      </Row>
    </>
  );

  const renderParsing = () => (
    <Column crossAxisSize="max" alignItems="center" style={{ padding: 32 }}>
      <Spinner />
      <SizedBox height={12} />
      <Text size={14}>{fieldsLang.uploadParsing}</Text>
    </Column>
  );

  const renderSelectValue = () => (
    <>
      <Text weight={500}>
        {fieldsLang.customLayerSelectValueTitle}
      </Text>
      <SizedBox height={12} />
      <Column crossAxisSize="max" style={{ gap: 8, maxHeight: 260, overflowY: "auto" }}>
        {candidates.map((prop) => (
          <div
            key={prop.key}
            onClick={() => setValueProperty(prop.key)}
            style={SELECTABLE_STYLE(valueProperty === prop.key)}
          >
            <Text size={14} weight={500}>
              {prop.key}
            </Text>
            <Text size={12} color="#6B7280">
              {fieldsLang.uploadSelectPropertyExample + ": " + (prop.example || "—")}
            </Text>
          </div>
        ))}
      </Column>
      <SizedBox height={16} />
      <Row justifyContent="space-between" style={{ gap: 8 }}>
        <Button
          kind="outline"
          onClick={() => {
            setValueProperty(null);
            setStep("ready");
          }}
        >
          {fieldsLang.customLayerNoValues}
        </Button>
        <Button onClick={() => setStep("ready")} disabled={!valueProperty}>
          {fieldsLang.uploadSelectPropertyApply}
        </Button>
      </Row>
    </>
  );

  const renderReady = () => (
    <>
      <Text weight={500}>
        {fieldsLang.customLayerReadyTitle +
          ": " +
          features.length +
          " " +
          fieldsLang.customLayerShapes}
      </Text>
      <SizedBox height={12} />
      <BoundaryPreviewMap features={features} />
      <SizedBox height={16} />
      <Input
        label={fieldsLang.customLayerNameLabel}
        value={layerName}
        onChange={(e) => setLayerName(e.target.value)}
      />
      {valueProperty && (
        <>
          <SizedBox height={16} />
          <Input
            label={fieldsLang.customLayerUnitLabel}
            placeholder="kg/ha"
            value={unitLabel}
            onChange={(e) => setUnitLabel(e.target.value)}
          />
        </>
      )}
      {errorBlock}
      <SizedBox height={24} />
      <Row justifyContent="space-between" style={{ gap: 8 }}>
        <Button kind="outline" onClick={startOver} disabled={importing}>
          {fieldsLang.uploadStartOver}
        </Button>
        <Button
          onClick={handleImport}
          disabled={features.length === 0 || layerName.trim() === "" || importing}
        >
          {importing
            ? fieldsLang.uploadImporting
            : fieldsLang.uploadDialogSaveButton}
        </Button>
      </Row>
    </>
  );

  return (
    <Dialog
      title={fieldsLang.customLayerAdd}
      visible={visible}
      onClose={onClose}
      style={{ maxWidth: 560 }}
    >
      {step === "source" && renderSource()}
      {step === "jdPick" && renderJdPick()}
      {step === "parsing" && renderParsing()}
      {step === "selectValue" && renderSelectValue()}
      {step === "ready" && renderReady()}
    </Dialog>
  );
};

export default observer(AddCustomLayerDialog);
